import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Product, CreateProductRequest } from '../types';
import { productService } from '../services/productService';
import { categoryService, unitTypeService, customFieldService, CustomProductField } from '../services/configurationService';
import { customToast } from '../utils/customToast';
import { X, Package, Calendar, DollarSign, AlertCircle, ListPlus } from 'lucide-react';

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product?: Product | null;
  onProductUpdated: () => void;
  isLoading?: boolean;
}

export const ProductModal: React.FC<ProductModalProps> = ({
  isOpen,
  onClose,
  product,
  onProductUpdated,
  isLoading = false
}) => {
  const { theme } = useTheme();
  const [isClosing, setIsClosing] = useState(false);
  const [categories, setCategories] = useState<Array<{ id: number; name: string }>>([]);
  const [units, setUnits] = useState<Array<{ id: number; name: string; short_name: string }>>([]);
  const [customFields, setCustomFields] = useState<CustomProductField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
  const [formData, setFormData] = useState<CreateProductRequest>({
    name: '',
    brand: '',
    category: '',
    expiry_date: '',
    stock_quantity: 0,
    minimum_stock_level: 10,
    purchase_price: 0,
    selling_price: 0,
    unit: '',
    description: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateProductRequest, string>>>({});

  // Load categories, units, and custom fields - preload immediately
  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        const [categoriesData, unitsData, customFieldsData] = await Promise.all([
          categoryService.getAll(true), // Only active categories
          unitTypeService.getAll(true),  // Only active units
          customFieldService.getAll(true) // Only active custom fields
        ]);
        
        const categoriesArray = Array.isArray(categoriesData) 
          ? categoriesData 
          : (categoriesData as any).data || [];
        const unitsArray = Array.isArray(unitsData) 
          ? unitsData 
          : (unitsData as any).data || [];
        const fieldsArray = Array.isArray(customFieldsData) 
          ? customFieldsData 
          : (customFieldsData as any).data || [];
        
        setCategories(categoriesArray);
        setUnits(unitsArray);
        setCustomFields(fieldsArray.sort((a: CustomProductField, b: CustomProductField) => a.sort_order - b.sort_order));
      } catch (error) {
        console.error('Failed to load configuration:', error);
        customToast.error('Failed to load categories and units');
      }
    };

    // Load data immediately, not just when modal opens
    loadConfiguration();
  }, []); // Run once on mount

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        brand: product.brand,
        category: product.category,
        expiry_date: product.expiry_date ? new Date(product.expiry_date).toISOString().split('T')[0] : '',
        stock_quantity: product.stock_quantity,
        minimum_stock_level: product.minimum_stock_level || 10,
        purchase_price: product.purchase_price,
        selling_price: product.selling_price,
        unit: product.unit,
        description: product.description || ''
      });
      
      // Load existing custom field values when editing
      if (product.custom_fields) {
        setCustomFieldValues(product.custom_fields);
      }
    } else {
      setFormData({
        name: '',
        brand: '',
        category: '',
        expiry_date: '',
        stock_quantity: 0,
        minimum_stock_level: 10,
        purchase_price: 0,
        selling_price: 0,
        unit: '',
        description: ''
      });
      // Clear custom field values when adding a new product
      setCustomFieldValues({});
    }
    setErrors({});
  }, [product, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreateProductRequest, string>> = {};

    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (formData.brand && !formData.brand.trim()) newErrors.brand = 'Brand name cannot be empty';
    if (!formData.category) newErrors.category = 'Category is required';
    
    // Convert to numbers for comparison to handle string inputs
    const stockQuantity = typeof formData.stock_quantity === 'string' ? parseFloat(formData.stock_quantity) : formData.stock_quantity;
    const purchasePrice = typeof formData.purchase_price === 'string' ? parseFloat(formData.purchase_price) : formData.purchase_price;
    const sellingPrice = typeof formData.selling_price === 'string' ? parseFloat(formData.selling_price) : formData.selling_price;
    
    if (stockQuantity < 0) newErrors.stock_quantity = 'Stock quantity cannot be negative';
    if (purchasePrice <= 0) newErrors.purchase_price = 'Purchase price must be greater than 0';
    if (sellingPrice <= 0) newErrors.selling_price = 'Selling price must be greater than 0';
    
    if (sellingPrice < purchasePrice) {
      newErrors.selling_price = 'Selling price must be greater than or equal to purchase price';
    }

    // Check if best before date is in the past
    if (formData.expiry_date) {
      const expiryDate = new Date(formData.expiry_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (expiryDate < today) {
        newErrors.expiry_date = 'Best before date cannot be in the past';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      customToast.error('Please fix the validation errors');
      return;
    }

    setIsSubmitting(true);

    try {
      // Merge custom fields into the request payload
      const dataToSave = {
        ...formData,
        custom_fields: customFieldValues
      };

      if (product) {
        await productService.updateProduct({ ...dataToSave, id: product.id });
        customToast.success('Product updated successfully!');
      } else {
        await productService.createProduct(dataToSave);
        customToast.success('Product added successfully!');
      }
      
      onProductUpdated();
      handleClose();
    } catch (error) {
      console.error('Failed to save Product:', error);
      customToast.error(error instanceof Error ? error.message : 'Failed to save Product');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? 0 : parseFloat(value) || 0) : 
              type === 'checkbox' ? (e.target as HTMLInputElement).checked :
              value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof CreateProductRequest]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCustomFieldChange = (fieldKey: string, value: any) => {
    setCustomFieldValues(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const renderCustomField = (field: CustomProductField) => {
    const value = customFieldValues[field.field_key] || field.default_value || '';

    const baseInputClasses = `w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-0 ${
      theme === 'dark'
        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
    }`;

    switch (field.field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.field_key, e.target.value)}
            placeholder={field.placeholder || field.field_name}
            required={field.is_required}
            className={baseInputClasses}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.field_key, e.target.value)}
            placeholder={field.placeholder || '0'}
            required={field.is_required}
            className={baseInputClasses}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.field_key, e.target.value)}
            required={field.is_required}
            className={baseInputClasses}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleCustomFieldChange(field.field_key, e.target.value)}
            placeholder={field.placeholder || field.field_name}
            required={field.is_required}
            rows={3}
            className={`${baseInputClasses} resize-none`}
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleCustomFieldChange(field.field_key, e.target.value)}
            required={field.is_required}
            className={baseInputClasses}
          >
            <option value="">Select {field.field_name}</option>
            {(field.field_options || field.options || []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => handleCustomFieldChange(field.field_key, e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-0"
            />
            <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'}`}>
              {field.help_text || 'Enable'}
            </span>
          </div>
        );

      default:
        return null;
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 150);
  };

  if (!isOpen && !isClosing) return null;

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] ${
        isClosing ? 'animate-fadeOut' : 'animate-fadeIn'
      }`}
      onClick={handleClose}
    >
      <div 
        className={`w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl ${
          isClosing ? 'animate-scaleOut' : 'animate-scaleIn'
        } ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b flex-shrink-0 rounded-t-xl ${
          theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'
              }`}>
                <Package className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${
                  theme === 'dark' ? 'text-white' : 'text-slate-900'
                }`}>
                  {product ? 'Edit Product' : 'Add New Product'}
                </h2>
                <p className={`text-xs truncate ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {product ? 'Update product information' : 'Enter product details'}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${
                theme === 'dark' 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Form Body */}
        <div className={`flex-1 overflow-y-auto ${
          theme === 'dark' ? 'bg-slate-800' : 'bg-white'
        }`}>
          {isLoading ? (
            // Loading Skeleton
            <div className="p-4 sm:p-5 md:p-6 space-y-6">
              {/* Section 1 Skeleton */}
              <div className="space-y-4">
                <div className={`h-6 w-40 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                }`} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(4)].map((_, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className={`h-4 w-24 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                      <div className={`h-10 w-full rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 2 Skeleton */}
              <div className="space-y-4">
                <div className={`h-6 w-40 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                }`} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(4)].map((_, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className={`h-4 w-24 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                      <div className={`h-10 w-full rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Fields Skeleton */}
              <div className="space-y-4">
                <div className={`h-6 w-40 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                }`} />
                <div className="space-y-3">
                  {[...Array(3)].map((_, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className={`h-4 w-32 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                      <div className={`h-10 w-full rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} id="product-form" className="p-4 sm:p-5 md:p-6">
              <div className="space-y-4 sm:space-y-5">
            {/* Basic Information */}
          <div className="space-y-4">
            <h3 className={`text-base font-medium flex items-center space-x-2 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              <Package className="w-5 h-5" />
              <span>Basic Information</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                }`}>
                  Product Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${
                    theme === 'dark'
                      ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 ${
                          errors.name ? 'border-red-500' : 'border-gray-600'
                        } focus:bg-gray-600`
                      : `bg-white text-gray-900 placeholder-gray-500 ${
                          errors.name ? 'border-red-300' : 'border-slate-300'
                        }`
                  }`}
                  placeholder="Enter product name"
                />
                {errors.name && (
                  <p className="mt-1 text-xs sm:text-sm text-red-400 flex items-center space-x-1">
                    <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>{errors.name}</span>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="brand" className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                }`}>
                  Brand Name *
                </label>
                <input
                  type="text"
                  id="brand"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${
                    theme === 'dark'
                      ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 ${
                          errors.brand ? 'border-red-500' : 'border-gray-600'
                        } focus:bg-gray-600`
                      : `bg-white text-gray-900 placeholder-gray-500 ${
                          errors.brand ? 'border-red-300' : 'border-slate-300'
                        }`
                  }`}
                  placeholder="Enter brand name"
                />
                {errors.brand && (
                  <p className="mt-1 text-xs sm:text-sm text-red-400 flex items-center space-x-1">
                    <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>{errors.brand}</span>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="category" className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                }`}>
                  Category *
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${
                    theme === 'dark'
                      ? `bg-gray-700 border-gray-600 text-white ${
                          errors.category ? 'border-red-500' : 'border-gray-600'
                        } focus:bg-gray-600`
                      : `bg-white text-gray-900 ${
                          errors.category ? 'border-red-300' : 'border-slate-300'
                        }`
                  }`}
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.name}>{category.name}</option>
                  ))}
                </select>
                {errors.category && (
                  <p className="mt-1 text-xs sm:text-sm text-red-400 flex items-center space-x-1">
                    <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>{errors.category}</span>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="unit" className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                }`}>
                  Unit Type
                </label>
                <select
                  id="unit"
                  name="unit"
                  value={formData.unit}
                  onChange={handleChange}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors touch-manipulation ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white focus:bg-gray-600'
                      : 'bg-white border-slate-300 text-gray-900'
                  }`}
                >
                  <option value="">Select Unit</option>
                  {units.map(unit => (
                    <option key={unit.id} value={unit.short_name}>
                      {unit.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Stock & Pricing */}
          <div className="space-y-3 sm:space-y-4">
            <h3 className={`text-base sm:text-lg font-medium flex items-center space-x-2 ${
              theme === 'dark' ? 'text-white' : 'text-slate-900'
            }`}>
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Stock & Pricing</span>
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <label htmlFor="stock_quantity" className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                }`}>
                  Stock Quantity
                </label>
                <input
                  type="number"
                  id="stock_quantity"
                  name="stock_quantity"
                  value={formData.stock_quantity === 0 ? '' : formData.stock_quantity}
                  onChange={handleChange}
                  min="0"
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg sm:rounded-xl transition-colors touch-manipulation ${
                    theme === 'dark'
                      ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 ${
                          errors.stock_quantity ? 'border-red-500' : 'border-gray-600'
                        } focus:bg-gray-600`
                      : `bg-white text-gray-900 placeholder-gray-500 ${
                          errors.stock_quantity ? 'border-red-300' : 'border-slate-300'
                        }`
                  }`}
                  placeholder="0"
                />
                {errors.stock_quantity && (
                  <p className="mt-1 text-sm text-red-400 flex items-center space-x-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.stock_quantity}</span>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="purchase_price" className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                }`}>
                  Purchase Price *
                </label>
                <input
                  type="number"
                  id="purchase_price"
                  name="purchase_price"
                  value={formData.purchase_price === 0 ? '' : formData.purchase_price}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg sm:rounded-xl transition-colors touch-manipulation ${
                    theme === 'dark'
                      ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 ${
                          errors.purchase_price ? 'border-red-500' : 'border-gray-600'
                        } focus:bg-gray-600`
                      : `bg-white text-gray-900 placeholder-gray-500 ${
                          errors.purchase_price ? 'border-red-300' : 'border-slate-300'
                        }`
                  }`}
                  placeholder="0.00"
                />
                {errors.purchase_price && (
                  <p className="mt-1 text-sm text-red-400 flex items-center space-x-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.purchase_price}</span>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="selling_price" className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                }`}>
                  Selling Price *
                </label>
                <input
                  type="number"
                  id="selling_price"
                  name="selling_price"
                  value={formData.selling_price === 0 ? '' : formData.selling_price}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg sm:rounded-xl transition-colors touch-manipulation ${
                    theme === 'dark'
                      ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 ${
                          errors.selling_price ? 'border-red-500' : 'border-gray-600'
                        } focus:bg-gray-600`
                      : `bg-white text-gray-900 placeholder-gray-500 ${
                          errors.selling_price ? 'border-red-300' : 'border-slate-300'
                        }`
                  }`}
                  placeholder="0.00"
                />
                {errors.selling_price && (
                  <p className="mt-1 text-sm text-red-400 flex items-center space-x-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.selling_price}</span>
                  </p>
                )}
              </div>
            </div>
          </div>
          {/* Additional Details */}
          <div className="space-y-4">
            <h3 className={`text-base font-medium flex items-center space-x-2 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              <Calendar className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`} />
              <span>Additional Details</span>
            </h3>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label htmlFor="expiry_date" className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                }`}>
                  Best Before Date
                </label>
                <input
                  type="date"
                  id="expiry_date"
                  name="expiry_date"
                  value={formData.expiry_date}
                  onChange={handleChange}
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg sm:rounded-xl transition-colors touch-manipulation ${
                    theme === 'dark'
                      ? `bg-gray-700 border-gray-600 text-white ${
                          errors.expiry_date ? 'border-red-500' : 'border-gray-600'
                        } focus:bg-gray-600`
                      : `bg-white text-gray-900 ${
                          errors.expiry_date ? 'border-red-300' : 'border-slate-300'
                        }`
                  }`}
                />
                {errors.expiry_date && (
                  <p className="mt-1 text-sm text-red-400 flex items-center space-x-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.expiry_date}</span>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="description" className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                }`}>
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg sm:rounded-xl transition-colors touch-manipulation resize-none ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600'
                      : 'bg-white border-slate-300 text-gray-900 placeholder-gray-500'
                  }`}
                  placeholder="Optional description or notes"
                />
              </div>
            </div>
          </div>

          {/* Custom Fields */}
          {customFields.length > 0 && (
            <div className="space-y-4">
              <h3 className={`text-base font-medium flex items-center space-x-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                <ListPlus className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`} />
                <span>Additional Fields</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {customFields.map((field) => (
                  <div key={field.id} className={field.field_type === 'textarea' ? 'md:col-span-2' : ''}>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {field.field_name}
                      {field.is_required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {renderCustomField(field)}
                    {field.help_text && field.field_type !== 'checkbox' && (
                      <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {field.help_text}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
            </div>
          </form>
            )}
        </div>

          {/* Footer with Action Buttons */}
          <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t flex-shrink-0 rounded-b-xl ${
            theme === 'dark' ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-gray-50'
          }`}>
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-3">
              {isLoading ? (
                <>
                  <div className={`w-full sm:flex-1 h-10 rounded-xl animate-pulse ${
                    theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                  }`} />
                  <div className={`w-full sm:flex-1 h-10 rounded-xl animate-pulse ${
                    theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                  }`} />
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className={`w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl border transition-all duration-200 touch-manipulation ${
                      theme === 'dark'
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="product-form"
                    disabled={isSubmitting}
                    className={`w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 touch-manipulation ${
                      theme === 'dark'
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isSubmitting ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : null}
                    <span>{product ? 'Update Product' : 'Add Product'}</span>
                  </button>
                </>
              )}
            </div>
          </div>
      </div>
    </div>,
    document.body
  );
};

export default ProductModal;
